<script setup lang="ts">
/**
 * Online CV page body: two mirrored content blocks with the web-card
 * screenshots (public link, QR code), the link-and-QR walkthrough beside a
 * dark panel, and one button. The page title band comes from
 * MarketingFeaturePage.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const blocks = [
  { id: 'online', image: '/web-sofax.png', points: ['instantShare', 'alwaysUpToDate', 'professionalUrl'], reverse: false },
  { id: 'qr', image: '/web-classic.png', points: ['easyAccess', 'networking', 'modern'], reverse: true },
] as const
</script>

<template>
  <section class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell relative z-10">
        <SofaxSectionHeading
          :eyebrow="t('homepage.sharing.eyebrow')"
          :title="t('homepage.sharing.title')"
          :text="t('homepage.sharing.subtitle')"
        />

        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- Alternating content blocks, each with a framed screenshot -->
          <div
            v-for="block in blocks"
            :key="block.id"
            class="grid grid-cols-1 items-center gap-10 md:gap-[3.75rem] lg:grid-cols-2 xl:gap-20"
          >
            <div class="img-shadow mx-auto w-full max-w-[31.25rem]" :class="block.reverse ? 'lg:order-2' : ''">
              <div class="overflow-hidden rounded-[0.625rem] border border-ink bg-white dark:border-gray-600">
                <NuxtImg
                  :src="block.image"
                  :alt="t(`homepage.sharing.benefits.${block.id}.title`)"
                  format="webp"
                  quality="85"
                  loading="lazy"
                  class="w-full"
                />
              </div>
            </div>

            <div :class="block.reverse ? 'lg:order-1' : ''">
              <div class="mb-[3.125rem]">
                <h2 class="!text-3xl md:!text-[2.5rem]">{{ t(`homepage.sharing.benefits.${block.id}.title`) }}</h2>
                <p class="mt-5">{{ t(`homepage.sharing.benefits.${block.id}.description`) }}</p>
              </div>
              <ul class="flex max-w-[35rem] flex-col gap-y-5">
                <li v-for="point in block.points" :key="point" class="display-heading display-heading-4 flex items-start gap-3">
                  <i class="bi bi-check-circle-fill shrink-0 text-3xl font-normal leading-none text-accent" />
                  <span>{{ t(`homepage.sharing.points.${point}`) }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Walkthrough: link, then QR, beside the dark panel -->
          <div class="grid grid-cols-1 items-center gap-10 md:gap-[3.75rem] lg:grid-cols-2 xl:grid-cols-[1fr_minmax(0,0.8fr)] xl:gap-20">
            <div>
              <div class="mb-[3.125rem]">
                <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('homepage.sharing.showcase.title') }}</h2>
                <p class="mt-5">{{ t('homepage.sharing.showcase.description') }}</p>
              </div>

              <div class="flex max-w-[35rem] flex-col gap-4">
                <div>
                  <span class="field-label">{{ t('homepage.sharing.showcase.publicUrl') }}</span>
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div class="field-input truncate !leading-normal text-muted dark:text-gray-400">inmyresume.com/u/your-profile</div>
                    <span class="btn btn-accent btn-sm btn-static shrink-0"><span>{{ t('homepage.sharing.showcase.instantShare') }}</span></span>
                  </div>
                </div>

                <div class="flex justify-center" aria-hidden="true">
                  <SofaxIconTile icon="bi-arrow-down" round size="sm" />
                </div>

                <div class="card border-accent bg-accent-soft/20 dark:bg-accent/10">
                  <div class="display-heading display-heading-5 mb-2">{{ t('homepage.sharing.showcase.qrCode') }}</div>
                  <p class="text-base">{{ t('homepage.sharing.showcase.qrCodeText') }}</p>
                </div>
              </div>
            </div>

            <div class="relative mx-auto w-full max-w-[31.25rem]">
              <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-center text-white dark:bg-gray-800 md:p-10">
                <div class="mx-auto flex size-28 items-center justify-center rounded-[0.625rem] bg-white text-ink" aria-hidden="true">
                  <i class="bi bi-qr-code text-6xl" />
                </div>
                <span class="mt-3 block text-sm !text-white/60">{{ t('homepage.sharing.showcase.qrExample') }}</span>
                <div class="display-heading display-heading-3 mb-3 mt-8 !text-white">{{ t('homepage.sharing.showcase.powered') }}</div>
                <p class="!text-white/75">{{ t('homepage.sharing.showcase.technology') }}</p>
              </div>
              <img
                src="/sofax/content-shape-1.svg"
                alt=""
                aria-hidden="true"
                width="125"
                height="137"
                class="pointer-events-none absolute -right-10 -top-10 -z-10 hidden xl:inline-block"
              >
            </div>
          </div>

          <div class="text-center">
            <SofaxBtn
              :to="isAuthenticated ? '/cv' : '/registration'"
              :label="t('homepage.sharing.cta')"
              variant="accent"
            />
          </div>
        </div>
      </div>

      <img
        src="/sofax/background-shape-6.svg"
        alt=""
        aria-hidden="true"
        width="174"
        height="171"
        class="pointer-events-none absolute -left-10 top-1/2 -z-10 hidden max-w-[153px] opacity-70 xl:inline-block"
      >
    </div>
  </section>
</template>
